import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, Calendar, ArrowUpRight, BookOpen, CheckCircle, PawPrint, Download, ChevronRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import BLOG_ARTICLES from "@/data/blogArticles";
import { C, DISPLAY, BODY, MONO, Reveal, EditorialLabel, EditorialHeader, Section, Heading, EBtn } from "@/styles/editorial";

const CATEGORY_ICONS = {
  PREPARA: CheckCircle,
  EMERGENCIAS: PawPrint,
};

/* Sort articles alphabetically by title */
const SORTED_ARTICLES = [...BLOG_ARTICLES].sort((a, b) => a.title.localeCompare(b.title, "es"));

export default function RecursosGuiasPage() {
  const navigate = useNavigate();

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
  };

  return (
    <div className="min-h-screen" style={{ background: C.white, fontFamily: BODY }}>
      <SEO
        title="Guías de protección animal en emergencias"
        description="Guías prácticas para proteger a tus animales en emergencias: incendios forestales, olas de calor, pirotecnia, kits de evacuación y más. Recursos para la ciudadanía."
        path="/recursos/ciudadania/guias"
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "Recursos", url: null },
          { name: "Ciudadanía", url: null },
          { name: "Guías", url: "/recursos/ciudadania/guias" },
        ]}
      />
      <EditorialHeader backLabel="Volver al Hub" backTo="/" />

      {/* BREADCRUMB */}
      <div className="px-6 md:px-12 pt-16 md:pt-20 pb-0" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto">
          <nav className="flex items-center gap-2 mb-6" aria-label="Breadcrumb" data-testid="guias-breadcrumb">
            {[
              { label: "Recursos", to: null },
              { label: "Ciudadanía", to: null },
              { label: "Guías", to: null },
            ].map((crumb, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <ChevronRight className="w-3 h-3" style={{ color: "rgba(255,255,255,0.3)" }} />}
                <span
                  className="text-[10px] font-semibold tracking-[0.15em] uppercase"
                  style={{ fontFamily: MONO, color: i === 2 ? C.orange : "rgba(255,255,255,0.5)" }}
                >
                  {crumb.label}
                </span>
              </span>
            ))}
          </nav>
        </div>
      </div>

      {/* HERO */}
      <section className="px-6 md:px-12 pt-4 pb-12 md:pt-6 md:pb-16" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <EditorialLabel text="RECURSOS PARA LA CIUDADANÍA" color={C.orange} />
            <h1
              className="text-[clamp(2.5rem,6vw,4.5rem)] leading-[1.02] tracking-tight mb-4"
              style={{ fontFamily: DISPLAY, color: C.white }}
              data-testid="guias-title"
            >
              Guías de{" "}
              <span style={{ color: C.orange }}>protección animal</span>
            </h1>
            <p className="text-lg max-w-2xl" style={{ color: "rgba(255,255,255,0.65)", lineHeight: 1.7 }}>
              Protocolos, consejos y recursos prácticos para proteger a tus animales en cualquier situación de emergencia.
              <strong style={{ color: C.white }}> Información que salva vidas.</strong>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ARTICLES — editorial list (alphabetically sorted) */}
      <Section bg={C.white} testId="guias-articles">
        <div className="max-w-[1100px]">
          <div className="space-y-6">
            {SORTED_ARTICLES.map((article, i) => {
              const CatIcon = CATEGORY_ICONS[article.category] || BookOpen;
              const hasDownloads = article.downloadables && article.downloadables.length > 0;
              return (
                <Reveal key={article.slug} delay={i * 0.06}>
                  <ArticleCard
                    article={article}
                    CatIcon={CatIcon}
                    hasDownloads={hasDownloads}
                    formatDate={formatDate}
                    navigate={navigate}
                    index={i}
                  />
                </Reveal>
              );
            })}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="px-6 md:px-12 py-20 md:py-28" style={{ background: C.navy }}>
        <div className="max-w-[900px] mx-auto text-center">
          <Reveal>
            <Heading size="md">
              <span style={{ color: C.white }}>¿Quieres estar </span>
              <span style={{ color: C.orange }}>preparado?</span>
            </Heading>
            <p className="text-base mt-4 mb-10 max-w-lg mx-auto" style={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.7 }}>
              Crea tu plan de emergencia personalizado, prepara tu mochila de vida y protege a tus animales.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <EBtn onClick={() => navigate("/prepara")} bg={C.orange} color={C.navy} testId="guias-cta-prepara">
                Ir a Prepara <ArrowUpRight className="w-4 h-4" />
              </EBtn>
              <EBtn onClick={() => navigate("/conecta")} bg={C.white} color={C.navy} testId="guias-cta-conecta">
                Conectar ahora <ArrowUpRight className="w-4 h-4" />
              </EBtn>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

/* ─── ARTICLE CARD (hover = lift + shadow) ─── */
function ArticleCard({ article, CatIcon, hasDownloads, formatDate, navigate, index }) {
  const [hovered, setHovered] = useState(false);
  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="cursor-pointer"
      onClick={() => navigate(`/recursos/ciudadania/guias/${article.slug}`)}
      data-testid={`guia-card-${article.slug}`}
      style={{
        background: C.white,
        border: `1px solid ${C.navy}10`,
        borderRadius: 4,
        overflow: "hidden",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered ? "0 16px 40px rgba(45,53,97,0.1)" : "none",
        transition: "transform 0.35s ease, box-shadow 0.35s ease",
      }}
    >
      <div className="grid md:grid-cols-12 items-stretch">
        {/* Color accent */}
        <div className="md:col-span-3 p-6 md:p-8 flex flex-col justify-between" style={{ background: article.categoryColor }}>
          <div>
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase block mb-2" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.7)" }}>
              {article.category}
            </span>
            <span className="text-4xl font-bold" style={{ fontFamily: DISPLAY, color: C.white }}>
              0{index + 1}
            </span>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <span className="text-[10px] flex items-center gap-1" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.6)" }}>
              <Clock className="w-3 h-3" /> {article.readTime}
            </span>
            {hasDownloads && (
              <span className="text-[10px] flex items-center gap-1" style={{ fontFamily: MONO, color: "rgba(255,255,255,0.6)" }}>
                <Download className="w-3 h-3" /> PDF
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="md:col-span-9 p-6 md:p-8 flex flex-col justify-center">
          <h2 className="text-lg md:text-xl lg:text-2xl tracking-tight mb-3" style={{ fontFamily: DISPLAY, color: C.navy }}>
            {article.title}
          </h2>
          <p className="text-sm leading-relaxed mb-4 max-w-xl" style={{ color: "#64748b" }}>
            {article.subtitle}
          </p>
          <div className="flex items-center justify-between">
            <span className="text-xs flex items-center gap-1" style={{ fontFamily: MONO, color: "#94a3b8" }}>
              <Calendar className="w-3 h-3" /> {formatDate(article.date)}
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-bold tracking-wider uppercase" style={{ fontFamily: MONO, color: article.categoryColor }}>
              Leer <ArrowRight className="w-3.5 h-3.5" style={{
                transform: hovered ? "translateX(3px)" : "translateX(0)",
                transition: "transform 0.3s ease",
              }} />
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}
